import React, { FC, useState, useEffect } from 'react'
import { useIntl, IntlShape } from 'react-intl'
import { ID } from '@/_metronic/helpers'
import { SalesRepartitionData } from '../core/_models'
import { getSalesRepartition } from '../core/_requests'
import { DonutCard } from './DonutCard'
import { TreemapCard } from './TreemapCard'

type ChartType = 'donut' | 'treemap';
const getLabel = (agg: string, slug: string, intl: IntlShape) => {
  if (['brand', 'product'].includes(agg)) {
    return slug
  }

  return intl.formatMessage({ id: `P2C.PRODUCT_${agg.toUpperCase()}_${slug}` })
}

type Props = {
  title: string
  chartType?: ChartType;
  userId?: ID
  agg: string
  from: Date
  to: Date
  timezone: string;
  height: number
  displayRow?: boolean
}

const SalesRepartitionChart: FC<Props> = ({ userId, agg, from, to, height, title, displayRow, timezone, chartType = 'donut' }) => {
  const intl = useIntl()
  const [data, setData] = useState<Array<SalesRepartitionData> | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const res = await getSalesRepartition({ agg, from, to, userId, timezone })
        setData(res || [])
      } catch (e: unknown) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [agg, userId, from, to])

  if (chartType === 'donut') {
    return (
      <DonutCard<SalesRepartitionData>
        id={`product-repartiton-${agg}`}
        title={title}
        height={height}
        displayRow={displayRow}
        isLoading={isLoading}
        data={(data || [])}
        showAggs={agg !== 'product'}
        getLabel={(item) => getLabel(agg, item._id, intl)}
        getAggValue={(item, agg) => {
          const v = Number(item[agg as keyof SalesRepartitionData]) || 0

          return ['cogs', 'soldAmount'].includes(agg) ? Math.round(v * 100) / 100 : v
        }}
      />
    )
  }

  return (
    <TreemapCard<SalesRepartitionData>
      id={`product-repartiton-${agg}`}
      title={title}
      height={height}
      isLoading={isLoading}
      data={(data || [])}
      showAggs={agg !== 'product'}
      getLabel={(item) => getLabel(agg, item._id, intl)}
      getAggValue={(item, agg) => {
        const v = Number(item[agg as keyof SalesRepartitionData]) || 0

        return ['cogs', 'soldAmount'].includes(agg) ? Math.round(v * 100) / 100 : v
      }}
    />
  )
}

export default SalesRepartitionChart
